import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useCartStore } from "@/stores/cartStore";
import { useCompareStore, COMPARE_LIMIT } from "@/stores/compareStore";
import { formatPrice, getProductVideo, getProductModel3d, type ShopifyProduct } from "@/lib/shopify";
import { Loader2, Check, GitCompare, Box } from "lucide-react";

interface Props {
  product: ShopifyProduct;
}

export function ProductCard({ product }: Props) {
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const compareItems = useCompareStore((s) => s.items);
  const toggleCompare = useCompareStore((s) => s.toggle);
  const inCompare = compareItems.some((p) => p.node.id === product.node.id);
  const compareFull = !inCompare && compareItems.length >= COMPARE_LIMIT;

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (compareFull) return;
    toggleCompare(product);
  };

  const node = product.node;
  const variant = node.variants.edges[0]?.node;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;
  const video = getProductVideo(node);
  const model3d = getProductModel3d(node);
  const has3d = Boolean(video || model3d);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoReady, setVideoReady] = useState(false);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  return (
    <div className="group block">
      <Link
        to="/shop/$handle"
        params={{ handle: node.handle }}
        className="block"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-secondary">
          {image ? (
            <img
              src={image.url}
              alt={image.altText ?? node.title}
              className={`h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.03] ${
                video && videoReady ? "opacity-0 group-hover:opacity-0" : "opacity-100"
              }`}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-luxe text-muted-foreground">
              No image
            </div>
          )}
          {video && (
            <video
              ref={videoRef}
              src={video.url}
              poster={image?.url}
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => setVideoReady(true)}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                videoReady ? "opacity-0 group-hover:opacity-100" : "opacity-0"
              }`}
            />
          )}
          {has3d && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 border border-hairline bg-background/85 px-2 py-1 text-[9px] uppercase tracking-luxe text-foreground backdrop-blur-sm">
              <Box className="h-3 w-3" strokeWidth={1.5} />
              360°
            </span>
          )}
          <button
            onClick={handleCompare}
            disabled={compareFull}
            aria-label={inCompare ? "Remove from compare" : "Add to compare"}
            className={`absolute right-3 top-3 flex h-9 items-center gap-1.5 border px-3 text-[10px] uppercase tracking-luxe transition-colors ${
              inCompare
                ? "border-foreground bg-foreground text-background"
                : "border-hairline bg-background/85 text-foreground backdrop-blur-sm hover:border-foreground"
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {inCompare ? (
              <Check className="h-3 w-3" strokeWidth={1.5} />
            ) : (
              <GitCompare className="h-3 w-3" strokeWidth={1.5} />
            )}
            <span>{inCompare ? "Comparing" : "Compare"}</span>
          </button>
        </div>
        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl leading-tight">{node.title}</h3>
            {node.options.length > 0 && node.options[0].values.length > 0 && (
              <p className="mt-1 text-xs uppercase tracking-luxe text-muted-foreground">
                {node.options
                  .map((o) => o.values.slice(0, 2).join(", "))
                  .slice(0, 2)
                  .join(" · ")}
              </p>
            )}
          </div>
          <span className="font-serif text-lg whitespace-nowrap">
            {formatPrice(price.amount, price.currencyCode)}
          </span>
        </div>
      </Link>
      <button
        onClick={handleAdd}
        disabled={isLoading || !variant}
        className="mt-4 flex h-11 w-full items-center justify-center border border-hairline text-xs uppercase tracking-luxe transition-colors hover:bg-foreground hover:text-background disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to Bag"}
      </button>
    </div>
  );
}
